import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FieldChips, type ChipItem, type ChipOption, type FieldChipsState } from "@/components/ui/FieldChips";
import { supabase } from "@/lib/supabase-client";

interface AbilityRef {
  id: string;
  name: string;
  description?: string;
}

type AbilityValue = { ability: AbilityRef; expires?: string | null };

interface AbilitiesInputProps {
  /** Entity's own abilities — array of { ability: { id, name, description? }, expires? } */
  value: AbilityValue[];
  /** Inherited abilities — each item can carry its own sourceLabel */
  inherited?: { ability: AbilityRef; sourceLabel?: string }[];
  /** Default label for inherited badges (used when item has no sourceLabel) */
  inheritedLabel?: string;
  /** Show expiry date support on chips (only for user screens). Default: false */
  showExpiry?: boolean;
  state?: FieldChipsState;
  label?: string;
  error?: string;
  onChange?: (abilities: AbilityValue[]) => void;
}

export function AbilitiesInput({
  value,
  inherited = [],
  inheritedLabel = "From Client",
  showExpiry = false,
  state = "editing",
  label = "Abilities",
  error,
  onChange,
}: AbilitiesInputProps) {
  const [options, setOptions] = useState<ChipOption[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch all ability options on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const { data, error: fetchError } = await supabase
          .schema("lists")
          .from("abilities")
          .select("id, name, description")
          .order("name", { ascending: true });
        if (fetchError) throw fetchError;
        if (cancelled) return;
        setOptions(
          (data ?? []).map((a) => ({
            id: a.id,
            name: a.name,
            description: a.description || undefined,
          })),
        );
      } catch (err) {
        console.error("Failed to load abilities:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Build chip items: inherited (readonly) + project's own (removable)
  const chipItems = useMemo<ChipItem[]>(() => {
    const chips: ChipItem[] = [];
    for (const ca of inherited) {
      chips.push({
        id: `inherited-${ca.ability.id}`,
        name: ca.ability.name,
        sourceLabel: ca.sourceLabel || inheritedLabel,
        readonly: true,
      });
    }
    for (const pa of value) {
      // Skip if already shown as inherited
      if (inherited.some((ca) => ca.ability.id === pa.ability.id)) continue;
      chips.push({
        id: pa.ability.id,
        name: pa.ability.name,
        subtitle: pa.ability.description,
        readonly: false,
        ...(showExpiry ? { expiry: pa.expires ?? null } : {}),
      });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inherited, inheritedLabel]);

  const handleAdd = useCallback(
    (abilityId: string) => {
      // Don't add if already in value
      if (value.some((pa) => pa.ability.id === abilityId)) return;
      const opt = options.find((o) => o.id === abilityId);
      if (!opt) return;
      onChange?.([...value, { ability: { id: opt.id, name: opt.name, description: opt.description } }]);
    },
    [value, options, onChange],
  );

  const handleRemove = useCallback(
    (chipId: string) => {
      onChange?.(value.filter((pa) => pa.ability.id !== chipId));
    },
    [value, onChange],
  );

  const handleExpiryChange = useCallback(
    (chipId: string, date: string | null) => {
      onChange?.(value.map((pa) => (pa.ability.id === chipId ? { ...pa, expires: date } : pa)));
    },
    [value, onChange],
  );

  const resolvedState: FieldChipsState = loading ? "loading" : state;

  return (
    <FieldChips
      label={label}
      icon="shield-check-outline"
      accentTone="emerald"
      items={chipItems}
      options={options}
      state={resolvedState}
      placeholder="Search abilities…"
      error={error}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onExpiryChange={showExpiry ? handleExpiryChange : undefined}
    />
  );
}
